import { Box, ButtonGroup } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import Loader from 'Components/Forms/Loader';
import SiteTable from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import usePartnerSites from 'FeatureModules/Partner/hooks/usePartnerSites';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import React, { FC } from 'react';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { ESiteViewOption } from 'FeatureModules/Site/types/site.types';
import ActionContainer from 'Components/ActionContainer';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const PartnerSites: FC = () => {
  const {
    sites,
    partner,
    error,
    loading,
    searchTerm,
    orderBy,
    viewOption,
    summary,
    totalCount,
    hasNextPage,
    handleSiteViewOptionChange,
    handleOrderByChange,
    handlePageEndReached,
    handleSearchTermChange,
  } = usePartnerSites();
  const { handleCreateSiteCluster, handleCreateSite } = useSiteActions();

  if (loading && sites === undefined) return <Loader />;
  if (error) return <ErrorState />;

  const FilterContainer = (
    <ButtonGroup size="small">
      <ThemeButton
        className={viewOption === ESiteViewOption.CLUSTER ? 'selected' : ''}
        onClick={() => handleSiteViewOptionChange(ESiteViewOption.CLUSTER)}
        typographyColor="ALMOST_BLACK"
      >
        Cluster view
      </ThemeButton>
      <ThemeButton
        className={viewOption === ESiteViewOption.LIST ? 'selected' : ''}
        onClick={() => handleSiteViewOptionChange(ESiteViewOption.LIST)}
        typographyColor="ALMOST_BLACK"
      >
        List view
      </ThemeButton>
    </ButtonGroup>
  );

  if (!sites.length && !searchTerm && !loading) {
    return (
      <PageContainer>
        <PageTitle
          title="Partner sites"
          goBackLabel="All partners"
          goBackUrl="/partners"
          titleExtension={partner?.name}
        />
        {/* Default filter is site cluster but theoretically there can be no cluster but sites and user would have no possibility to change the filter with the empty state  */}
        <ActionContainer mt={2}>{FilterContainer}</ActionContainer>

        <Box mt={4}>
          <EmptyStateCard
            data-testid="sites-getting-started-card"
            data-testid-button="create-site-btn"
            title="Add the partner sites you know of to give your collaborator a head start"
            variant={EItemTypes.LOCATION}
            buttonText="Add new site"
            background={EBackgroundTypes.WORLD}
            size={EAvailableSizes.LARGE}
            renderIllustration={
              <CardIllustration
                image={EIllustrationTypes.LOCATIONS}
                width={300}
                right={25}
                top={0}
              />
            }
            onCreateItemClick={handleCreateSite}
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle
        title="Partner sites"
        goBackLabel="All partners"
        goBackUrl="/partners"
        titleExtension={partner?.name}
      />

      <FlexBox my={2} gap={1}>
        {FilterContainer}

        <Box ml={1} display="flex" gap={1}>
          <ThemeButton
            size="medium"
            color="WHITE"
            onClick={handleCreateSiteCluster}
            startIcon={<Plus size={20} color="black" />}
            data-testid="create-cluster-btn"
          >
            Add cluster
          </ThemeButton>
          <ThemeButton
            size="medium"
            color="PRIMARY"
            onClick={() => handleCreateSite()}
            startIcon={<Plus size={20} color="white" />}
            data-testid="create-site-btn"
          >
            Add site
          </ThemeButton>
        </Box>
      </FlexBox>

      <SiteTable
        maxHeight="calc(100vh - 280px)"
        sites={sites}
        loading={loading}
        totalCount={totalCount}
        summary={summary}
        orderBy={orderBy}
        hasMoreRows={hasNextPage}
        searchTerm={searchTerm}
        onEndReached={handlePageEndReached}
        onOrderByChange={handleOrderByChange}
        onSearchTermChange={handleSearchTermChange}
      />
    </PageContainer>
  );
};

export default PartnerSites;
